import { useCurrentStudio } from '~entities/studios/model/studio-store';

import type { TrialEnrollment } from '~entities/student/modal/student';

import {
  formatTrialDate,
  formatTrialDayOfWeek,
  formatTrialTimeRange,
  TRIAL_CARD_DATE_PATTERN,
} from '~shared/helpers/format-trial-datetime';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Card, CardContent, CardTitle } from '~shared/shadcn/ui/card';

export interface TrialClassCardProps {
  trial: TrialEnrollment;
}

export function TrialClassCard({ trial }: TrialClassCardProps) {
  const studioTimezone = useCurrentStudio()?.timezone;
  const title =
    trial.classId?.title ?? trial.className ?? 'Trial Class';
  const timeStr =
    formatTrialTimeRange(trial.startTime, trial.endTime, studioTimezone) ?? '-';

  const days =
    (trial.classId?.days?.length ?? 0) > 0
      ? trial.classId!.days!
      : trial.startTime
        ? [formatTrialDayOfWeek(trial.startTime, studioTimezone) ?? '-']
        : [];

  const startDateFormatted = trial.startTime
    ? (formatTrialDate(trial.startTime, studioTimezone, TRIAL_CARD_DATE_PATTERN) ??
      '-')
    : '-';
  const endDateFormatted = trial.endTime
    ? (formatTrialDate(trial.endTime, studioTimezone, TRIAL_CARD_DATE_PATTERN) ??
      startDateFormatted)
    : startDateFormatted;

  const instructor =
    trial.classId?.instructor?.[0]?.fieldName ?? '-';

  return (
    <Card className={cn('min-h-[19.625rem] w-[21.125rem]')}>
      <CardContent className='mt-6'>
        <div className='flex items-center justify-between'>
          <CardTitle className={cn('mb-2 mt-3.5 text-lg leading-[1.688rem]')}>
            {title}
          </CardTitle>
          <Badge className='bg-amber-500 text-white hover:bg-amber-500'>
            Trial Class
          </Badge>
        </div>
        <p className={cn('mb-2 text-sm leading-[1.313rem] text-black')}>
          {timeStr}
        </p>
        <ul className={cn('flex flex-wrap gap-2.5')}>
          {days.map((day) => (
            <li key={day}>
              <Badge
                className={cn(
                  'flex items-center justify-center px-1 text-xs',
                )}
              >
                {day}
              </Badge>
            </li>
          ))}
        </ul>

        <div className={cn('mt-6 flex justify-between')}>
          <div className={cn('flex flex-col gap-6')}>
            <div className={cn('flex flex-col gap-1')}>
              <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                Main Teacher
              </h4>
              <p className={cn('text-sm leading-[1.313rem]')}>
                {instructor}
              </p>
            </div>
            <div className={cn('flex flex-col gap-1')}>
              <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                Class Starts
              </h4>
              <p className={cn('text-sm leading-[1.313rem]')}>
                {startDateFormatted}
              </p>
            </div>
          </div>

          <div className={cn('flex flex-col gap-6')}>
            <div className={cn('flex-1')}></div>
            <div className={cn('flex flex-col gap-1')}>
              <h4 className={cn('text-sm font-semibold leading-[1.313rem]')}>
                Class End
              </h4>
              <p className={cn('text-sm leading-[1.313rem]')}>
                {endDateFormatted}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
